import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Card, CardActionArea, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, ToggleButton, ToggleButtonGroup, Stack, Typography,} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import { Stage, Layer, Image as KonvaImage, Text as KonvaText, Rect } from "react-konva";
import useImage from "use-image";
import { useParams } from "react-router-dom";
import { api } from "../../services/api";
import { notify } from "../../utils/notifyToast";
import { fitTextToBox } from "../../utils/textFit";
import SelectTarifa from "./SelTarifa";
import "./fuentes.css";

const CARD_WIDTH = 216;
const CARD_HEIGHT = 136;

// imagen individual
const PreviewImageNode = ({ element, scale }) => {
  const [image] = useImage(element.imgURL, "anonymous");
  return (
    <KonvaImage
      image={image}
      x={element.x * scale}
      y={element.y * scale}
      width={element.width * scale}
      height={element.height * scale}
      rotation={element.rotation || 0}
      listening={false}
    />
  );
};

// placeholder del qr
const PreviewQRNode = ({ element, scale }) => {
  const [image] = useImage("/qr.png");
  return (
    <KonvaImage
      image={image}
      x={element.x * scale}
      y={element.y * scale}
      width={element.width * scale}
      height={element.height * scale}
      listening={false}
    />
  );
};

// miniatura del diseño completo
const DesignPreview = ({ elements, designWidth, designHeight }) => {
  const scaleX = CARD_WIDTH / (designWidth || 100);
  const scaleY = CARD_HEIGHT / (designHeight || 50);
  const scale = Math.min(scaleX, scaleY);
  const stageWidth = (designWidth || 100) * scale;
  const stageHeight = (designHeight || 50) * scale;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: CARD_HEIGHT,
        backgroundColor: "#e0e0e0",
        overflow: "hidden",
      }}
    >
      <Stage width={stageWidth} height={stageHeight} listening={false}>
        <Layer>
          <Rect x={0} y={0} width={stageWidth} height={stageHeight} fill="white" />
          {elements?.map((el, idx) => {
            if (el.type === "image") {
              return <PreviewImageNode key={el.id ?? idx} element={el} scale={scale} />;
            }
            if (el.type === "qr") {
              return <PreviewQRNode key={el.id ?? idx} element={el} scale={scale} />;
            }
            // todos los tipos de texto
            const width = el.width || 50;
            const height = el.height || 20;
            const fontSize = el.fontSize || 12;
            const fitted = fitTextToBox({
              text: el.text,
              width,
              height,
              fontSize,
              fontFamily: el.fontFamily,
              fontStyle: el.fontStyle,
            });
            return (
              <KonvaText
                key={el.id ?? idx}
                x={el.x * scale}
                y={el.y * scale}
                width={width * scale}
                height={fitted.height * scale}
                text={el.text || ""}
                wrap="word"
                fontSize={fitted.fontSize * scale}
                fontFamily={el.fontFamily || "Arial"}
                fill={el.fill || "#000000"}
                align={el.align || "left"}
                fontStyle={el.fontStyle || "normal"}
                textDecoration={el.textDecoration || ""}
                rotation={el.rotation || 0}
                listening={false}
              />
            );
          })}
        </Layer>
      </Stage>
    </Box>
  );
};

const DisenosGallery = ({ onEditar, onNuevo }) => {
  const param = useParams();

  const [disenos, setDisenos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState("todos");

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [disenoSeleccionado, setDisenoSeleccionado] = useState(null);

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteWarningMsg, setDeleteWarningMsg] = useState("");
  const [loadingAction, setLoadingAction] = useState(false);

  const [openDuplicar, setOpenDuplicar] = useState(false);

  const cargarDisenos = async () => {
    setLoading(true);
    try {
      const response = await api.impresion.getAll(param.id);
      if (response.status === "success" && response.data.disenos) {
        setDisenos(response.data.disenos);
      } else {
        setDisenos([]);
      }
    } catch (err) {
      console.error(err);
      setDisenos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDisenos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disenosParsed = useMemo(() =>
    disenos.map((d) => {
      try {
        return { ...d, parsed: JSON.parse(d.json) };
      } catch {
        return { ...d, parsed: null };
      }
    }),
    [disenos]
  );

  const disenosFiltrados = useMemo(() =>
    filtroTipo === "todos"
      ? disenosParsed
      : disenosParsed.filter((d) => d.parsed?.type?.type === filtroTipo),
    [disenosParsed, filtroTipo]
  );

  const handleMenuOpen = (event, diseno) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setDisenoSeleccionado(diseno);
  };

  const handleMenuClose = () => setMenuAnchor(null);

  const handleEditar = () => {
    handleMenuClose();
    onEditar(disenoSeleccionado.id);
  };

  const handleCrearCopia = () => {
    handleMenuClose();
    setOpenDuplicar(true);
  };

  const handleGuardarComoPlantilla = () => {
    handleMenuClose();
    setOpenDuplicar(true);
  };

  const handleDuplicarConfirm = async (tarifaDestino) => {
    setOpenDuplicar(false);
    if (!tarifaDestino) return;
    setLoadingAction(true);
    try {
      const response = await api.impresion.duplicar(
        param.id,
        disenoSeleccionado.id,
        tarifaDestino.id
      );
      if (response.status === "success") {
        notify("success", "Diseño duplicado correctamente");
        cargarDisenos();
      } else {
        notify("error", "Error al duplicar: " + response.message);
      }
    } catch {
      notify("error", "Ocurrió un error al duplicar");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleEliminarClick = async () => {
    handleMenuClose();
    setLoadingAction(true);
    try {
      const response = await api.impresion.deleteDiseno(param.id, disenoSeleccionado.id);
      if (response.status === "warning") {
        setDeleteWarningMsg(response.message);
        setOpenConfirmDelete(true);
      } else if (response.status === "success") {
        notify("success", "Diseño eliminado correctamente");
        cargarDisenos();
      } else {
        notify("error", "Error al eliminar: " + response.message);
      }
    } catch {
      notify("error", "Ocurrió un error al eliminar");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleConfirmForceDelete = async () => {
    setOpenConfirmDelete(false);
    setLoadingAction(true);
    try {
      const response = await api.impresion.deleteDiseno(
        param.id,
        disenoSeleccionado.id,
        true
      );
      if (response.status === "success") {
        notify("success", "Diseño eliminado correctamente");
        cargarDisenos();
      } else {
        notify("error", "Error al eliminar: " + response.message);
      }
    } catch {
      notify("error", "Ocurrió un error al eliminar");
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <Box sx={{ p: 4, pt: 0, maxWidth: 1200, isolation: "isolate" }}>

      {/* FILTRO Y NUEVO */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <ToggleButtonGroup
          value={filtroTipo}
          exclusive
          onChange={(_, val) => val && setFiltroTipo(val)}
          size="small"
          sx={{
            bgcolor: "white",
            borderRadius: "7px",
            border: "1px solid rgba(86,86,255,0.18)",
            //p: "4px",
            gap: 0.5,
            "& .MuiToggleButtonGroup-grouped": {
              border: "none !important",
              borderRadius: "7px !important",
            },
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: "7px",
              px: 2.5,
              py: 0.9,
              minWidth: 90,
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              color: "#5656ff",
              transition: `
                background-color 450ms ease,
                color 350ms ease,
                transform 420ms cubic-bezier(0.4, 0, 0.2, 1),
                min-width 500ms cubic-bezier(0.4, 0, 0.2, 1),
                padding 500ms cubic-bezier(0.4, 0, 0.2, 1),
                box-shadow 420ms ease
              `,
              "&:hover": {
                bgcolor: "rgba(86,86,255,0.06)",
              },
              "&.Mui-selected": {
                bgcolor: "#5656ff",
                color: "white",
                minWidth: 120,
                px: 3,
                transform: "scale(1.03)",
                boxShadow:
                  "0 6px 18px rgba(86,86,255,0.18)",
                "&:hover": {
                  bgcolor: "#4b4be8",
                },
              },
            },
          }}
        >
          <ToggleButton value="todos">Todos</ToggleButton>
          <ToggleButton value="tarjeta">Tarjetas</ToggleButton>
          <ToggleButton value="pulsera">Pulseras</ToggleButton>
        </ToggleButtonGroup>
        <Button sx={{
          //marginBottom: "20px",
          borderRadius: "7px",
          backgroundColor: "#5656FF",
          padding: "4px 18px",
          color: "white",
          fontFamily: "Inter, sans-serif",
        }} variant="contained" startIcon={<AddIcon />} onClick={onNuevo}>
          Nuevo diseño
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : disenosFiltrados.length === 0 ? (
        <Alert severity="info">
          {filtroTipo === "todos"
            ? "Todavía no hay diseños. Creá uno nuevo con el botón de arriba."
            : `No hay diseños de tipo ${filtroTipo === "tarjeta" ? "tarjeta" : "pulsera"} todavía.`}
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tickets diseñados hasta hoy...
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
            {disenosFiltrados.map((diseno) => (
                <Card sx={{ width: '100%', position: "relative" }} elevation={2} key={diseno.id}>
                  <CardActionArea onClick={() => onEditar(diseno.id)}>
                    <DesignPreview
                      elements={diseno.parsed?.elements ?? []}
                      designWidth={diseno.parsed?.type?.width}
                      designHeight={diseno.parsed?.type?.height}
                    />
                    <CardContent sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ color: "#0b0062" }} fontWeight="bold" noWrap>
                        {diseno.tarifa}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#4B4D63", fontFamily: "Inter, sans-serif" }} noWrap>
                        {diseno.parsed?.type?.type === "tarjeta" ? "Tarjeta" :
                          diseno.parsed?.type?.type === "pulsera" ? "Pulsera" :
                            "Tipo desconocido"}
                      </Typography>
                    </CardContent>
                  </CardActionArea>

                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      zIndex: 1,
                      bgcolor: "rgba(255,255,255,0.75)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                    }}
                    onClick={(e) => handleMenuOpen(e, diseno)}
                    disabled={loadingAction}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Card>
            ))}
          </Box>
        </>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        sx={{borderRadius: '8px',}}
      >
        <Box sx={{
          borderBottom: '1px solid rgba(75, 77, 99, 0.08)',
          pb: 0.5,
        }}>

          <Typography
            variant="caption"
            sx={{ px: 2, py: 0.5, display: "block", fontFamily: "Bricolage Grotesque", fontWeight: "600", fontSize: "14px", color: "#0b0062" }}
          >
            {disenoSeleccionado?.tarifa}
          </Typography>
        </Box>
        <MenuItem sx={{
          fontSize: "14px",
          color: "#4B4D63", fontFamily: "Inter, sans-serif",
          mx: 0.5,
          px: 1.5,
          py: 0.75,
          '&:hover': {
            backgroundColor: 'rgba(11, 0, 98, 0.06)',
            color: '#0b0062',
            '& .MuiListItemIcon-root': {
              color: '#0b0062',
            },
          },
        }} onClick={handleEditar}>Editar ticket</MenuItem>
        <MenuItem sx={{
          fontSize: "14px",
          color: "#4B4D63", fontFamily: "Inter, sans-serif",
          mx: 0.5,
          px: 1.5,
          py: 0.75,
          '&:hover': {
            backgroundColor: 'rgba(11, 0, 98, 0.06)',
            color: '#0b0062',
            '& .MuiListItemIcon-root': {
              color: '#0b0062',
            },
          },
        }} onClick={handleCrearCopia}>Crear una copia</MenuItem>
        <MenuItem sx={{
          fontSize: "14px",
          color: "#4B4D63", fontFamily: "Inter, sans-serif",
          mx: 0.5,
          px: 1.5,
          py: 0.75,
          '&:hover': {
            backgroundColor: 'rgba(11, 0, 98, 0.06)',
            color: '#0b0062',
            '& .MuiListItemIcon-root': {
              color: '#0b0062',
            },
          },
        }} onClick={handleGuardarComoPlantilla}>Guardar como plantilla</MenuItem>
        <MenuItem sx={{
          fontSize: "14px",
          color: "error.main", fontFamily: "Inter, sans-serif",
          mx: 0.5,
          px: 1.5,
          py: 0.75,
          '&:hover': {
            backgroundColor: 'rgba(11, 0, 98, 0.06)',
            color: '#0b0062',
            '& .MuiListItemIcon-root': {
              color: '#0b0062',
            },
          },
        }} onClick={handleEliminarClick}>
          Eliminar
        </MenuItem>
      </Menu>

      {/* CONFIRMAR DELETE */}
      <Dialog open={openConfirmDelete} onClose={() => setOpenConfirmDelete(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {deleteWarningMsg}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDelete(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmForceDelete}
            disabled={loadingAction}
            startIcon={loadingAction ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Eliminar de todas formas
          </Button>
        </DialogActions>
      </Dialog>

      {openDuplicar && disenoSeleccionado && (
        <SelectTarifa
          open={openDuplicar}
          onClose={() => setOpenDuplicar(false)}
          onConfirm={handleDuplicarConfirm}
          onSave={null}
          elements={disenoSeleccionado.parsed?.elements ?? []}
          dimensionContainer={disenoSeleccionado.parsed?.type ?? {}}
        />
      )}
    </Box>
  );
};

export default DisenosGallery;